<script setup lang="ts">
  import {
    type SuperUserOrganisation as Organisation,
    type PatchedSuperUserOrganisationRequest as PatchedOrganisationRequest,
    type StripeCustomersListResponse,
    type SuperUserStripeCustomer as StripeCustomer,
    type LawFields,
    type SuperUserHubSpotCompany as HubSpotCompany,
    type SuperUserOrganisation,
    type SuperUserStripeSubscription as StripeSubscription,
    type HubSpotCompanyCandidate,
    type StripeCustomerCandidate
  } from '@/types/generated/types.gen'
  import {
    OrganisationsService,
    StripeCustomersService,
    LawFieldsService,
    HubspotService,
    StripeSubscriptionsService
  } from '@/types/generated/services.gen'
  import { type IntegrityCheck } from '@/types/generated/types.gen'
  import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import DatePickerField from '@/components/admin/form/DatePickerField.vue'
  import router from '@/router'
  import { type GenericObject } from 'vee-validate'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import * as yup from 'yup'
  import { useTranslation } from '@/composables/useTranslation'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import { $SuperUserOrganisation as $Organisation } from '@/types/generated'
  import FormGrid from '@/components/admin/form/FormGrid.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import MultiSelectField from '@/components/admin/form/MultiSelectField.vue'
  import { toast } from '@/components/ui/toast'
  import { Button } from '@/components/ui/button'
  import { Badge } from '@/components/ui/badge'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'

  const { t } = useTranslation({
    en: {
      general: 'General',
      features: 'Features',
      botSettings: 'Bot Settings',
      viewBotSettings: 'Chatbot',

      name: 'Lawirm Name',
      street: 'Street',
      house: 'House',
      postcode: 'Postcode',
      city: 'City',
      country: 'Country',
      email: 'Email',
      phone: 'Phone',
      seats: 'Seats',
      appointmentBookingSeats: 'Appointment Booking Seats',
      connections: 'Connections',
      stripeConnection: 'Stripe Connection',
      lawFields: 'Law Fields',
      jupusPlatform: 'JUPUS Platform',
      jupusPlatformActive: 'Active',
      jupusPhoneAI: 'JUPUS Phone AI',
      jupusPhoneAIActive: 'Active',
      appointments: 'Appointments',
      documentAI: 'Document AI',
      aiWorkflows: 'AI Workflows',
      aiEnabled: 'AI Enabled',
      jupusPlatformSettings: 'JUPUS Platform Settings',
      monsumCustomerNumber: 'Monsum Customer Number',
      isTest: 'Test Organization',
      inboundEmailSlug: 'JUPUS Email',
      enableInboundEmailReplies: 'Enable Email Replies from Clients in JUPUS',
      canConfigureEmail: 'Custom Email Server',
      isNewUiSwitchEnabled: 'Enable New UI for Testing (can switch freely)',
      isForcedToUseNewUi: 'Enable New UI by Default (cannot switch)',
      uiSettings: 'UI Settings',
      onboardingStartDate: 'Onboarding Start Date',
      onboardingTime: 'Onboarding Time',
      hubspotConnection: 'HubSpot Connection',
      hasChurned: 'Has Churned',
      activeCustomer: 'Active Customer',
      churnedCustomer: 'Churned Customer',
      jupusPhonebotSettings: 'JUPUS Phonebot Settings',
      isPhonebotSelfServiceCustomer: 'Is Phonebot Self-Service Customer',
      phonebotSelfServicePlan: 'Phonebot Self-Service Plan',
      phonebotEnterpriseIncludedMinutes: 'Enterprise Included Minutes',
      phonebotLanguage: 'Phonebot Language',
      // HubSpot modal translations
      findHubspotMatch: 'Find HubSpot Company',
      hubspotNotConnected: 'No HubSpot company connected',
      hubspotNotConnectedDescription: 'Click below to search for and connect a HubSpot company.',
      connectHubspot: 'Connect HubSpot Company',
      removeConnection: 'Remove',
      hubspotModalTitle: 'Connect HubSpot Company',
      suggestedMatches: 'Suggested Matches',
      suggestedMatchesDescription: 'Based on email domains from this organisation',
      allCompanies: 'All HubSpot Companies',
      searchPlaceholder: 'Search by name or domain...',
      selectCompany: 'Select',
      noSuggestionsFound: 'No automatic matches found',
      noResultsFound: 'No companies found',
      searching: 'Searching...',
      close: 'Close',
      openInHubSpot: 'Open in HubSpot',
      change: 'Change',
      unnamedCompany: 'Unnamed Company',
      unnamed: 'Unnamed',
      orSearchAllCompanies: 'or search all companies',
      typeToSearch: 'Type to search for HubSpot companies...',
      matched: 'Matched',
      // Error messages
      error: 'Error',
      hubspotConnectError: 'Failed to connect HubSpot company. Please try again.',
      hubspotRemoveError: 'Failed to remove HubSpot connection. Please try again.',
      churnedStatusError: 'Failed to update customer status. Please try again.',
      customerNumber: 'Customer Number',
      // Onboarding date/time validation
      onboardingInvalidDateFormat: 'Invalid date format',
      onboardingInvalidDateFormatDescription: 'Date must be in DD.MM.YYYY format',
      onboardingInvalidDate: 'Invalid date',
      onboardingInvalidDateDescription: 'Date components must be valid numbers',
      onboardingInvalidTimeFormat: 'Invalid time format',
      onboardingInvalidTimeFormatDescription: 'Time must be in HH:MM format',
      onboardingInvalidTime: 'Invalid time',
      onboardingInvalidTimeDescription: 'Time components must be valid numbers',
      onboardingInvalidDateTime: 'Invalid date/time',
      onboardingInvalidDateTimeDescription: 'The provided date and time combination is not valid',
      // Churn confirmation dialog
      churnConfirmTitle: 'Confirm Customer Status Change',
      churnConfirmDescription:
        'Are you sure you want to mark this customer as churned? This may trigger data cleanup processes.',
      churnConfirmAction: 'Yes, mark as churned',
      cancel: 'Cancel',
      emailCopied: 'Email Copied',
      emailCopyError: 'Failed to copy email to clipboard',
      copyEmail: 'Copy',
      platformChurnedAt: 'Churn Date',
      platformChurnedAtHint: 'Last date the customer will have access to JUPUS Platform',
      phonebotChurnedAt: 'Churn Date',
      phonebotChurnedAtHint: 'Last date the customer will have access to JUPUS Phone AI',
      customerLabel: 'Customer',
      testCustomerLabel: 'Test Customer',
      setAsCustomer: 'Set as Customer',
      setAsTestCustomer: 'Set as Test Customer',
      chatbotSettings: 'Chatbot Settings',
      // Integrity check
      integrityCheck: 'Integrity Check',
      runIntegrityCheck: 'Run Integrity Check',
      integrityCheckError: 'Failed to run integrity check. Please try again.',
      integrityLastChecked: 'Last checked',
      integrityAllPassed: 'All checks passed',
      integrityHasErrors: 'Integrity issues found',
      integrityNeverChecked: 'Never checked',
      integrityCheckRunning: 'Running integrity checks...',
      noIntegrityChecks: 'No integrity checks available',
      // Subscription selector
      platformSubscription: 'Platform Subscription',
      phonebotSubscription: 'Phonebot Subscription',
      subscriptionNotConnected: 'No subscription connected',
      subscriptionNotConnectedDescription: 'Click below to search for and connect a subscription.',
      connectSubscription: 'Connect Subscription',
      subscriptionModalTitle: 'Select Subscription',
      availableSubscriptions: 'Available Subscriptions',
      noSubscriptionsFound: 'No subscriptions found',
      noStripeCustomer: 'Please connect a Stripe customer first',
      subscriptionStatus: 'Status',
      subscriptionPlans: 'Plans',
      subscriptionStartDate: 'Start Date',
      subscriptionTrialEnd: 'Trial End',
      subscriptionTrialPeriod: 'Trial Period',
      subscriptionCanceledAt: 'Canceled At',
      subscriptionCancelAtPeriodEnd: 'Cancels at period end',
      subscriptionCurrentPeriodEnd: 'Current Period End',
      subscriptionMrr: 'MRR',
      subscriptionArr: 'ARR',
      openInStripe: 'Open in Stripe',
      subscriptionConnectError: 'Failed to connect subscription. Please try again.',
      subscriptionRemoveError: 'Failed to remove subscription. Please try again.',
      loadingSubscriptions: 'Loading subscriptions...',
      // Stripe customer finder
      stripeNotConnected: 'No Stripe customer connected',
      stripeNotConnectedDescription: 'Click below to search for and connect a Stripe customer.',
      connectStripe: 'Connect Stripe Customer',
      stripeModalTitle: 'Connect Stripe Customer',
      stripeSuggestedMatches: 'Suggested Matches',
      stripeSuggestedMatchesDescription: 'Based on email domains from this organisation',
      allStripeCustomers: 'All Stripe Customers',
      stripeSearchPlaceholder: 'Search by name or email...',
      selectCustomer: 'Select',
      noStripeSuggestionsFound: 'No automatic matches found',
      noStripeResultsFound: 'No customers found',
      orSearchAllStripeCustomers: 'or search all customers',
      stripeTypeToSearch: 'Type to search for Stripe customers...',
      stripeConnectError: 'Failed to connect Stripe customer. Please try again.',
      stripeRemoveError: 'Failed to remove Stripe connection. Please try again.'
    },
    de: {
      general: 'Allgemein',
      features: 'Features',
      botSettings: 'Bot Einstellungen',
      viewBotSettings: 'Chatbot',
      name: 'Name der Kanzlei',
      street: 'Straße',
      house: 'Haus',
      postcode: 'PLZ',
      city: 'Stadt',
      country: 'Land',
      email: 'Email',
      phone: 'Telefon',
      seats: 'Seats',
      appointmentBookingSeats: 'Terminbuchung Seats',
      connections: 'Verbindungen',
      stripeConnection: 'Stripe Verbindung',
      lawFields: 'Rechtsgebiete',
      jupusPlatform: 'JUPUS Plattform',
      jupusPlatformActive: 'Aktiv',
      jupusPhoneAI: 'JUPUS Phone AI',
      jupusPhoneAIActive: 'Aktiv',
      appointments: 'Terminbuchung',
      documentAI: 'Dokumenten-KI',
      aiWorkflows: 'KI-Workflows',
      aiEnabled: 'KI aktivieren',
      jupusPlatformSettings: 'JUPUS Plattform Einstellungen',
      monsumCustomerNumber: 'Monsum Kundennummer',
      isTest: 'Test-Organisation',
      inboundEmailSlug: 'JUPUS Email',
      enableInboundEmailReplies: 'E-Mail Antwort von Mandanten in JUPUS',
      canConfigureEmail: 'Eigener E-Mail-Server',
      isNewUiSwitchEnabled: 'Neues UI zum Ausprobieren aktivieren (kann beliebig wechseln)',
      isForcedToUseNewUi: 'Neues UI standardmäßig aktivieren (kann nicht wechseln)',
      uiSettings: 'UI Einstellungen',
      onboardingStartDate: 'Onboarding Startdatum',
      onboardingTime: 'Onboarding Uhrzeit',
      hubspotConnection: 'HubSpot Verbindung',
      hasChurned: 'Hat gekündigt',
      activeCustomer: 'Aktiver Kunde',
      churnedCustomer: 'Gekündigter Kunde',
      jupusPhonebotSettings: 'JUPUS Phonebot Einstellungen',
      isPhonebotSelfServiceCustomer: 'Ist Phonebot Self-Service Kunde',
      phonebotSelfServicePlan: 'Phonebot Self-Service Plan',
      phonebotEnterpriseIncludedMinutes: 'Enterprise Inklusivminuten',
      phonebotLanguage: 'Phonebot Sprache',
      // HubSpot modal translations
      findHubspotMatch: 'HubSpot-Unternehmen finden',
      hubspotNotConnected: 'Kein HubSpot-Unternehmen verbunden',
      hubspotNotConnectedDescription:
        'Klicken Sie unten, um ein HubSpot-Unternehmen zu suchen und zu verbinden.',
      connectHubspot: 'HubSpot-Unternehmen verbinden',
      removeConnection: 'Entfernen',
      hubspotModalTitle: 'HubSpot-Unternehmen verbinden',
      suggestedMatches: 'Vorgeschlagene Übereinstimmungen',
      suggestedMatchesDescription: 'Basierend auf E-Mail-Domains dieser Organisation',
      allCompanies: 'Alle HubSpot-Unternehmen',
      searchPlaceholder: 'Nach Name oder Domain suchen...',
      selectCompany: 'Auswählen',
      noSuggestionsFound: 'Keine automatischen Übereinstimmungen gefunden',
      noResultsFound: 'Keine Unternehmen gefunden',
      searching: 'Suche läuft...',
      close: 'Schließen',
      openInHubSpot: 'In HubSpot öffnen',
      change: 'Ändern',
      unnamedCompany: 'Unbenanntes Unternehmen',
      unnamed: 'Unbenannt',
      orSearchAllCompanies: 'oder alle Unternehmen durchsuchen',
      typeToSearch: 'Zum Suchen von HubSpot-Unternehmen tippen...',
      matched: 'Übereinstimmung',
      // Error messages
      error: 'Fehler',
      hubspotConnectError:
        'HubSpot-Unternehmen konnte nicht verbunden werden. Bitte versuchen Sie es erneut.',
      hubspotRemoveError:
        'HubSpot-Verbindung konnte nicht entfernt werden. Bitte versuchen Sie es erneut.',
      churnedStatusError:
        'Kundenstatus konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
      // Onboarding date/time validation
      onboardingInvalidDateFormat: 'Ungültiges Datumsformat',
      onboardingInvalidDateFormatDescription: 'Datum muss im Format TT.MM.JJJJ sein',
      onboardingInvalidDate: 'Ungültiges Datum',
      onboardingInvalidDateDescription: 'Datumskomponenten müssen gültige Zahlen sein',
      onboardingInvalidTimeFormat: 'Ungültiges Zeitformat',
      onboardingInvalidTimeFormatDescription: 'Zeit muss im Format HH:MM sein',
      onboardingInvalidTime: 'Ungültige Zeit',
      onboardingInvalidTimeDescription: 'Zeitkomponenten müssen gültige Zahlen sein',
      onboardingInvalidDateTime: 'Ungültiges Datum/Uhrzeit',
      onboardingInvalidDateTimeDescription:
        'Die angegebene Datums- und Zeitkombination ist nicht gültig',
      customerNumber: 'Kundennummer',
      // Churn confirmation dialog
      churnConfirmTitle: 'Kundenstatusänderung bestätigen',
      churnConfirmDescription:
        'Sind Sie sicher, dass Sie diesen Kunden als gekündigt markieren möchten? Dies kann Datenbereinigungsprozesse auslösen.',
      churnConfirmAction: 'Ja, als gekündigt markieren',
      cancel: 'Abbrechen',
      emailCopied: 'E-Mail kopiert',
      emailCopyError: 'E-Mail konnte nicht in die Zwischenablage kopiert werden',
      copyEmail: 'Kopieren',
      platformChurnedAt: 'Kündigungsdatum',
      platformChurnedAtHint: 'Letzter Tag, an dem der Kunde Zugang zur JUPUS Plattform hat',
      phonebotChurnedAt: 'Kündigungsdatum',
      phonebotChurnedAtHint: 'Letzter Tag, an dem der Kunde Zugang zu JUPUS Phone AI hat',
      customerLabel: 'Kunde',
      testCustomerLabel: 'Testkunde',
      setAsCustomer: 'Als Kunde markieren',
      setAsTestCustomer: 'Als Testkunde markieren',
      chatbotSettings: 'Chatbot Einstellungen',
      // Integrity check
      integrityCheck: 'Integritätsprüfung',
      runIntegrityCheck: 'Integritätsprüfung durchführen',
      integrityCheckError: 'Integritätsprüfung fehlgeschlagen. Bitte erneut versuchen.',
      integrityLastChecked: 'Zuletzt geprüft',
      integrityAllPassed: 'Alle Prüfungen bestanden',
      integrityHasErrors: 'Integritätsprobleme gefunden',
      integrityNeverChecked: 'Noch nie geprüft',
      integrityCheckRunning: 'Integritätsprüfung läuft...',
      noIntegrityChecks: 'Keine Integritätsprüfungen verfügbar',
      // Subscription selector
      platformSubscription: 'Plattform-Abonnement',
      phonebotSubscription: 'Phonebot-Abonnement',
      subscriptionNotConnected: 'Kein Abonnement verbunden',
      subscriptionNotConnectedDescription:
        'Klicken Sie unten, um ein Abonnement zu suchen und zu verbinden.',
      connectSubscription: 'Abonnement verbinden',
      subscriptionModalTitle: 'Abonnement auswählen',
      availableSubscriptions: 'Verfügbare Abonnements',
      noSubscriptionsFound: 'Keine Abonnements gefunden',
      noStripeCustomer: 'Bitte verbinden Sie zuerst einen Stripe-Kunden',
      subscriptionStatus: 'Status',
      subscriptionPlans: 'Pläne',
      subscriptionStartDate: 'Startdatum',
      subscriptionTrialEnd: 'Testphase Ende',
      subscriptionTrialPeriod: 'Testphase',
      subscriptionCanceledAt: 'Gekündigt am',
      subscriptionCancelAtPeriodEnd: 'Kündigt zum Periodenende',
      subscriptionCurrentPeriodEnd: 'Aktuelle Periode endet',
      subscriptionMrr: 'MRR',
      subscriptionArr: 'ARR',
      openInStripe: 'In Stripe öffnen',
      subscriptionConnectError:
        'Abonnement konnte nicht verbunden werden. Bitte versuchen Sie es erneut.',
      subscriptionRemoveError:
        'Abonnement konnte nicht entfernt werden. Bitte versuchen Sie es erneut.',
      loadingSubscriptions: 'Abonnements werden geladen...',
      // Stripe customer finder
      stripeNotConnected: 'Kein Stripe-Kunde verbunden',
      stripeNotConnectedDescription:
        'Klicken Sie unten, um einen Stripe-Kunden zu suchen und zu verbinden.',
      connectStripe: 'Stripe-Kunde verbinden',
      stripeModalTitle: 'Stripe-Kunde verbinden',
      stripeSuggestedMatches: 'Vorgeschlagene Übereinstimmungen',
      stripeSuggestedMatchesDescription: 'Basierend auf E-Mail-Domains dieser Organisation',
      allStripeCustomers: 'Alle Stripe-Kunden',
      stripeSearchPlaceholder: 'Nach Name oder E-Mail suchen...',
      selectCustomer: 'Auswählen',
      noStripeSuggestionsFound: 'Keine automatischen Übereinstimmungen gefunden',
      noStripeResultsFound: 'Keine Kunden gefunden',
      orSearchAllStripeCustomers: 'oder alle Kunden durchsuchen',
      stripeTypeToSearch: 'Zum Suchen von Stripe-Kunden tippen...',
      stripeConnectError:
        'Stripe-Kunde konnte nicht verbunden werden. Bitte versuchen Sie es erneut.',
      stripeRemoveError:
        'Stripe-Verbindung konnte nicht entfernt werden. Bitte versuchen Sie es erneut.'
    }
  })

  const org = ref<SuperUserOrganisation | undefined>(undefined)
  const stripeCustomersList = ref<StripeCustomersListResponse>()
  const stripeCustomersOptions = computed(() => {
    return (
      stripeCustomersList.value?.results.map((customer) => ({
        value: customer.uuid,
        title: customer.name?.toString() + ' - ' + customer.email?.toString()
      })) ?? []
    )
  })
  const stripeCustomer = computed<StripeCustomer | undefined>(() => {
    if (!org.value?.stripe_customer_uuid) return undefined
    return stripeCustomersList.value?.results.find(
      (customer) => customer.uuid === org.value?.stripe_customer_uuid
    )
  })

  const { lawFieldTranslations } = useLawFieldTranslation()

  const lawFields = ref<LawFields>()
  const lawFieldOptions = computed(() => {
    const translations = lawFieldTranslations.value
    return (
      Object.keys(lawFields.value?.data ?? {}).map((key) => ({
        value: key,
        title: translations[key] || key
      })) ?? []
    )
  })

  // Integrity check state
  const isRunningIntegrityCheck = ref(false)
  const showIntegrityModal = ref(false)
  const integrityChecks = ref<IntegrityCheck[]>([])

  const runIntegrityCheck = async () => {
    if (!org.value) return
    integrityChecks.value = []
    showIntegrityModal.value = true
    isRunningIntegrityCheck.value = true
    try {
      const response = await OrganisationsService.organisationsRunIntegrityCheckCreate({
        uuid: org.value.uuid
      })
      // Handle both paginated response and direct array response
      integrityChecks.value = Array.isArray(response) ? response : (response.results ?? [])
      // Reload org to update has_integrity_errors and integrity_last_checked_at
      await reloadOrganization()
    } catch (error) {
      integrityChecks.value = []
      toast({
        title: t('error'),
        description: t('integrityCheckError'),
        variant: 'destructive'
      })
    } finally {
      isRunningIntegrityCheck.value = false
    }
  }

  const closeIntegrityModal = () => {
    showIntegrityModal.value = false
  }

  // HubSpot modal state
  const showHubspotModal = ref(false)
  const hubspotCandidates = ref<HubSpotCompanyCandidate[]>([])
  const hubspotSearchResults = ref<HubSpotCompany[]>([])
  const hubspotSearchQuery = ref('')
  const isSearchingCandidates = ref(false)
  const isSearchingAll = ref(false)
  const isSelectingHubspotCompany = ref<string | null>(null) // tracks uuid of company being selected

  const openHubspotModal = async () => {
    showHubspotModal.value = true
    hubspotSearchQuery.value = ''
    hubspotSearchResults.value = []

    // Automatically search for candidates when opening modal
    if (org.value) {
      isSearchingCandidates.value = true
      try {
        const response = await HubspotService.hubspotCompaniesFindForOrganisationList({
          organisationUuid: org.value.uuid
        })
        hubspotCandidates.value = response.results ?? []
      } finally {
        isSearchingCandidates.value = false
      }
    }
  }

  const closeHubspotModal = () => {
    showHubspotModal.value = false
    hubspotCandidates.value = []
    hubspotSearchResults.value = []
    hubspotSearchQuery.value = ''
    isSelectingHubspotCompany.value = null
  }

  const searchHubspotCompanies = async () => {
    if (!hubspotSearchQuery.value.trim()) {
      hubspotSearchResults.value = []
      return
    }

    isSearchingAll.value = true
    try {
      const response = await HubspotService.hubspotCompaniesList({
        search: hubspotSearchQuery.value,
        limit: 20
      })
      hubspotSearchResults.value = response.results ?? []
    } finally {
      isSearchingAll.value = false
    }
  }

  // Debounced search
  let searchTimeout: ReturnType<typeof setTimeout> | null = null
  watch(hubspotSearchQuery, () => {
    if (searchTimeout) clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      searchHubspotCompanies()
    }, 300)
  })

  // Cleanup timeout on component unmount to prevent memory leaks
  onBeforeUnmount(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout)
      searchTimeout = null
    }
  })

  const selectHubspotCompany = async (company: { uuid: string }) => {
    if (!org.value) return

    isSelectingHubspotCompany.value = company.uuid
    try {
      await handleSave({ hubspot_company_uuid: company.uuid })
      closeHubspotModal()
    } catch (error) {
      toast({
        title: t('error'),
        description: t('hubspotConnectError'),
        variant: 'destructive'
      })
    } finally {
      isSelectingHubspotCompany.value = null
    }
  }

  const removeHubspotConnection = async () => {
    if (!org.value) return

    try {
      await handleSave({ hubspot_company_uuid: null })
    } catch (error) {
      toast({
        title: t('error'),
        description: t('hubspotRemoveError'),
        variant: 'destructive'
      })
    }
  }

  // Stripe customer modal state
  const showStripeModal = ref(false)
  const stripeCandidates = ref<StripeCustomerCandidate[]>([])
  const stripeSearchResults = ref<StripeCustomer[]>([])
  const stripeSearchQuery = ref('')
  const isSearchingStripeCandidates = ref(false)
  const isSearchingStripeAll = ref(false)
  const isSelectingStripeCustomer = ref<string | null>(null)

  const openStripeModal = async () => {
    showStripeModal.value = true
    stripeSearchQuery.value = ''
    stripeSearchResults.value = []

    // Automatically search for candidates when opening modal
    if (org.value) {
      isSearchingStripeCandidates.value = true
      try {
        const response = await StripeCustomersService.stripeCustomersFindForOrganisationList({
          organisationUuid: org.value.uuid
        })
        stripeCandidates.value = response.results ?? []
      } finally {
        isSearchingStripeCandidates.value = false
      }
    }
  }

  const closeStripeModal = () => {
    showStripeModal.value = false
    stripeCandidates.value = []
    stripeSearchResults.value = []
    stripeSearchQuery.value = ''
    isSelectingStripeCustomer.value = null
  }

  const searchStripeCustomers = async () => {
    if (!stripeSearchQuery.value.trim()) {
      stripeSearchResults.value = []
      return
    }

    isSearchingStripeAll.value = true
    try {
      const response = await StripeCustomersService.stripeCustomersList({
        search: stripeSearchQuery.value,
        limit: 20
      })
      stripeSearchResults.value = response.results ?? []
    } finally {
      isSearchingStripeAll.value = false
    }
  }

  // Debounced search for Stripe
  let stripeSearchTimeout: ReturnType<typeof setTimeout> | null = null
  watch(stripeSearchQuery, () => {
    if (stripeSearchTimeout) clearTimeout(stripeSearchTimeout)
    stripeSearchTimeout = setTimeout(() => {
      searchStripeCustomers()
    }, 300)
  })

  // Cleanup timeout on component unmount
  onBeforeUnmount(() => {
    if (stripeSearchTimeout) {
      clearTimeout(stripeSearchTimeout)
      stripeSearchTimeout = null
    }
  })

  const selectStripeCustomer = async (customer: { uuid: string }) => {
    if (!org.value) return

    isSelectingStripeCustomer.value = customer.uuid
    try {
      await handleSave({ stripe_customer_uuid: customer.uuid })
      closeStripeModal()
    } catch (error) {
      toast({
        title: t('error'),
        description: t('stripeConnectError'),
        variant: 'destructive'
      })
    } finally {
      isSelectingStripeCustomer.value = null
    }
  }

  const removeStripeConnection = async () => {
    if (!org.value) return

    try {
      await handleSave({ stripe_customer_uuid: null })
    } catch (error) {
      toast({
        title: t('error'),
        description: t('stripeRemoveError'),
        variant: 'destructive'
      })
    }
  }

  // Subscription modal state
  type SubscriptionType = 'platform' | 'phonebot'
  const showSubscriptionModal = ref(false)
  const subscriptionModalType = ref<SubscriptionType>('platform')
  const availableSubscriptions = ref<StripeSubscription[]>([])
  const isLoadingSubscriptions = ref(false)
  const isSelectingSubscription = ref<string | null>(null)

  const openSubscriptionModal = async (type: SubscriptionType) => {
    if (!org.value?.stripe_customer_uuid) {
      toast({
        title: t('error'),
        description: t('noStripeCustomer'),
        variant: 'destructive'
      })
      return
    }

    subscriptionModalType.value = type
    showSubscriptionModal.value = true
    isLoadingSubscriptions.value = true

    try {
      const response = await StripeSubscriptionsService.stripeSubscriptionsList({
        customerUuid: org.value.stripe_customer_uuid,
        limit: 100
      })
      availableSubscriptions.value = (response.results ?? []) as StripeSubscription[]
    } catch (error) {
      toast({
        title: t('error'),
        description: t('subscriptionConnectError'),
        variant: 'destructive'
      })
    } finally {
      isLoadingSubscriptions.value = false
    }
  }

  const closeSubscriptionModal = () => {
    showSubscriptionModal.value = false
    availableSubscriptions.value = []
    isSelectingSubscription.value = null
  }

  const selectSubscription = async (subscription: StripeSubscription) => {
    if (!org.value) return

    isSelectingSubscription.value = subscription.uuid
    try {
      const fieldName =
        subscriptionModalType.value === 'platform'
          ? 'platform_stripe_subscription_uuid'
          : 'phonebot_stripe_subscription_uuid'
      await handleSave({ [fieldName]: subscription.uuid })
      closeSubscriptionModal()
    } catch (error) {
      toast({
        title: t('error'),
        description: t('subscriptionConnectError'),
        variant: 'destructive'
      })
    } finally {
      isSelectingSubscription.value = null
    }
  }

  const removeSubscription = async (type: SubscriptionType) => {
    if (!org.value) return

    try {
      const fieldName =
        type === 'platform'
          ? 'platform_stripe_subscription_uuid'
          : 'phonebot_stripe_subscription_uuid'
      await handleSave({ [fieldName]: null })
    } catch (error) {
      toast({
        title: t('error'),
        description: t('subscriptionRemoveError'),
        variant: 'destructive'
      })
    }
  }

  // Helper to format date
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  // Helper to format currency
  const formatCurrency = (amount: string | number | null | undefined): string => {
    if (amount === null || amount === undefined) return '-'
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(num)
  }

  // Helper to get subscription status color
  const getSubscriptionStatusColor = (status: string | null | undefined): string => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
      case 'trialing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
      case 'canceled':
      case 'incomplete_expired':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
      case 'past_due':
      case 'unpaid':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  const toggleTestStatus = () => {
    if (!org.value) return

    const newValue = !org.value.is_test
    handleSave({ is_test: newValue }).catch(() => {
      toast({
        title: t('error'),
        description: t('error'),
        variant: 'destructive'
      })
    })
  }

  // Onboarding date/time management
  const onboardingDate = ref<string | null>(null)
  const onboardingTime = ref<string>('09:30')

  // Initialize onboarding date/time from org
  watch(
    () => org.value?.onboarding_start_date,
    (newDate) => {
      if (newDate) {
        const date = new Date(newDate)
        // Format as DD.MM.YYYY
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        onboardingDate.value = `${day}.${month}.${year}`
        // Format time as HH:MM
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        onboardingTime.value = `${hours}:${minutes}`
      } else {
        onboardingDate.value = null
        onboardingTime.value = '09:30'
      }
    },
    { immediate: true }
  )

  const saveOnboardingDateTime = async (value) => {
    if (!org.value) return

    // If no date value is provided, clear the onboarding_start_date
    if (!value) {
      await handleSave({ onboarding_start_date: null })
      return
    }

    // Validate and parse date components
    const dateParts = value.split('.')
    if (dateParts.length !== 3) {
      toast({
        title: t('onboardingInvalidDateFormat'),
        description: t('onboardingInvalidDateFormatDescription'),
        variant: 'destructive'
      })
      return
    }

    const [day, month, year] = dateParts
    const dayNum = Number(day)
    const monthNum = Number(month)
    const yearNum = Number(year)

    if (isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum)) {
      toast({
        title: t('onboardingInvalidDate'),
        description: t('onboardingInvalidDateDescription'),
        variant: 'destructive'
      })
      return
    }

    // Validate and parse time components
    const timeParts = onboardingTime.value.split(':')
    if (timeParts.length !== 2) {
      toast({
        title: t('onboardingInvalidTimeFormat'),
        description: t('onboardingInvalidTimeFormatDescription'),
        variant: 'destructive'
      })
      return
    }

    const [hours, minutes] = timeParts
    const hoursNum = Number(hours)
    const minutesNum = Number(minutes)

    if (isNaN(hoursNum) || isNaN(minutesNum)) {
      toast({
        title: t('onboardingInvalidTime'),
        description: t('onboardingInvalidTimeDescription'),
        variant: 'destructive'
      })
      return
    }

    // Create date and validate it's a valid date
    const date = new Date(yearNum, monthNum - 1, dayNum, hoursNum, minutesNum)

    if (isNaN(date.getTime())) {
      toast({
        title: t('onboardingInvalidDateTime'),
        description: t('onboardingInvalidDateTimeDescription'),
        variant: 'destructive'
      })
      return
    }

    // Save the valid date
    await handleSave({ onboarding_start_date: date.toISOString() })
  }

  // Churn date management
  const platformChurnedAt = ref<string | null>(null)
  const phonebotChurnedAt = ref<string | null>(null)

  // Helper to convert date to DD.MM.YYYY format for the picker
  // Handles both ISO format (YYYY-MM-DD) and German format (DD.MM.YYYY)
  const formatDateForPicker = (dateStr: string | null | undefined): string | null => {
    if (!dateStr) return null

    // Check if already in German format (DD.MM.YYYY)
    const germanMatch = dateStr.match(/^(\d{2})\.(\d{2})\.(\d{4})/)
    if (germanMatch) {
      return dateStr // Already in correct format
    }

    // Handle ISO date string like "2025-01-01" or "2025-01-01T00:00:00"
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (isoMatch) {
      const [, year, month, day] = isoMatch
      return `${day}.${month}.${year}`
    }

    return null
  }

  // Initialize churn dates from org only once on initial load
  // We track whether user has touched these fields to prevent reload from overwriting
  const platformChurnedAtTouched = ref(false)
  const phonebotChurnedAtTouched = ref(false)

  watch(
    () => org.value?.platform_churned_at,
    (newDate) => {
      // Only sync from backend if user hasn't touched this field
      if (!platformChurnedAtTouched.value) {
        platformChurnedAt.value = formatDateForPicker(newDate)
      }
    },
    { immediate: true }
  )

  watch(
    () => org.value?.phonebot_churned_at,
    (newDate) => {
      // Only sync from backend if user hasn't touched this field
      if (!phonebotChurnedAtTouched.value) {
        phonebotChurnedAt.value = formatDateForPicker(newDate)
      }
    },
    { immediate: true }
  )

  // Helper to convert DD.MM.YYYY to ISO date string
  // Build ISO string directly to avoid timezone issues
  const parseDateToISO = (value: string | null): string | null => {
    if (!value) return null

    const dateParts = value.split('.')
    if (dateParts.length !== 3) return null

    const [day, month, year] = dateParts
    const dayNum = Number(day)
    const monthNum = Number(month)
    const yearNum = Number(year)

    if (isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum)) return null

    // Validate the date exists using UTC to avoid timezone issues
    const date = new Date(Date.UTC(yearNum, monthNum - 1, dayNum))
    if (
      date.getUTCFullYear() !== yearNum ||
      date.getUTCMonth() !== monthNum - 1 ||
      date.getUTCDate() !== dayNum
    ) {
      return null
    }

    // Return ISO format directly (YYYY-MM-DD)
    return `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`
  }

  const savePlatformChurnedAt = async (value: string | null) => {
    if (!org.value) return
    platformChurnedAtTouched.value = true

    // Allow clearing with empty/null value
    if (!value) {
      ;(org.value as any).platform_churned_at = null
      await handleSave({ platform_churned_at: null })
      platformChurnedAtTouched.value = false
      return
    }

    const isoDate = parseDateToISO(value)
    // Only update if parsing succeeded, leave existing value on invalid input
    if (isoDate === null) {
      return
    }
    ;(org.value as any).platform_churned_at = isoDate
    await handleSave({ platform_churned_at: isoDate })
    platformChurnedAtTouched.value = false
  }

  const savePhonebotChurnedAt = async (value: string | null) => {
    if (!org.value) return
    phonebotChurnedAtTouched.value = true

    // Allow clearing with empty/null value
    if (!value) {
      ;(org.value as any).phonebot_churned_at = null
      await handleSave({ phonebot_churned_at: null })
      phonebotChurnedAtTouched.value = false
      return
    }

    const isoDate = parseDateToISO(value)
    // Only update if parsing succeeded, leave existing value on invalid input
    if (isoDate === null) {
      return
    }
    ;(org.value as any).phonebot_churned_at = isoDate
    await handleSave({ phonebot_churned_at: isoDate })
    phonebotChurnedAtTouched.value = false
  }

  onMounted(async () => {
    org.value = (await OrganisationsService.organisationsRetrieve({
      uuid: router.currentRoute.value.params.uuid.toString()
    })) as Organisation
    stripeCustomersList.value = await StripeCustomersService.stripeCustomersList({ limit: 0 })
    lawFields.value = await LawFieldsService.lawFieldsRetrieve()
  })

  // Function to reload the organization data
  const reloadOrganization = async () => {
    if (org.value) {
      org.value = (await OrganisationsService.organisationsRetrieve({
        uuid: org.value.uuid
      })) as Organisation
    }
  }

  const handleSave = async (values: GenericObject) => {
    await OrganisationsService.organisationsPartialUpdate({
      uuid: org.value!.uuid,
      requestBody: values as PatchedOrganisationRequest
    })

    // Reload the organization data after save to get the updated information
    await reloadOrganization()
  }

  // Computed property to extract the email domain from the full inbound email address
  const inboundEmailDomain = computed(() => {
    if (!org.value?.inbound_email_address) return ''
    const atIndex = org.value.inbound_email_address.indexOf('@')
    if (atIndex === -1) return ''
    return org.value.inbound_email_address.substring(atIndex)
  })

  // Copy the full email address to clipboard
  const copyEmailToClipboard = async () => {
    if (!org.value?.inbound_email_address) return

    try {
      await navigator.clipboard.writeText(org.value.inbound_email_address)
      toast({
        title: t('emailCopied'),
        description: org.value.inbound_email_address
      })
    } catch (error) {
      toast({
        title: t('error'),
        description: t('emailCopyError'),
        variant: 'destructive'
      })
    }
  }
</script>

<template>
  <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
    <template v-if="org">
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Customers', to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST },
            {
              title: org.name || org.uuid,
              to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST.replace(':uuid', org.uuid)
            }
          ]"
        />

        <div class="flex items-center gap-4">
          <!-- Integrity Check Status -->
          <div class="flex items-center gap-2">
            <Badge
              variant="outline"
              :class="
                !org.integrity_last_checked_at
                  ? 'border-gray-300 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400'
                  : org.has_integrity_errors
                    ? `border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950
                      dark:text-amber-400`
                    : `border-green-300 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-950
                      dark:text-green-400`
              "
            >
              <span
                class="mr-1.5 h-2 w-2 rounded-full"
                :class="
                  !org.integrity_last_checked_at
                    ? 'bg-gray-400'
                    : org.has_integrity_errors
                      ? 'bg-amber-500'
                      : 'bg-green-500'
                "
              />
              <template v-if="!org.integrity_last_checked_at">
                {{ t('integrityNeverChecked') }}
              </template>
              <template v-else-if="org.has_integrity_errors">
                {{ t('integrityHasErrors') }}
              </template>
              <template v-else>
                {{ t('integrityAllPassed') }}
              </template>
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              class="h-7 w-7 p-0"
              :disabled="isRunningIntegrityCheck"
              @click="runIntegrityCheck"
            >
              <svg
                v-if="isRunningIntegrityCheck"
                class="h-4 w-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <svg
                v-else
                class="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </Button>
          </div>

          <!-- Chatbot Settings Link -->
          <Button
            variant="outline"
            size="sm"
            as-child
          >
            <router-link
              :to="ROUTES.ADMIN.CUSTOMERS.CUSTOMER_BOT_SETTINGS.replace(':uuid', org.uuid)"
              class="flex items-center gap-2"
            >
              <svg
                class="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
              {{ t('chatbotSettings') }}
              <svg
                class="h-3.5 w-3.5 opacity-60"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </router-link>
          </Button>

          <!-- Customer Type Status -->
          <div class="flex items-center gap-2">
            <Badge
              variant="outline"
              :class="
                org.is_test
                  ? `border-purple-300 bg-purple-50 text-purple-700 dark:border-purple-700 dark:bg-purple-950
                    dark:text-purple-400`
                  : `border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-600 dark:bg-slate-900
                    dark:text-slate-300`
              "
            >
              <span
                class="mr-1.5 h-2 w-2 rounded-full"
                :class="org.is_test ? 'bg-purple-500' : 'bg-slate-500'"
              />
              {{ org.is_test ? t('testCustomerLabel') : t('customerLabel') }}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Button
                  variant="ghost"
                  size="sm"
                  class="h-7 w-7 p-0"
                >
                  <svg
                    class="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                :align="'end'"
                class="w-[220px]"
              >
                <DropdownMenuItem
                  v-if="org.is_test"
                  class="cursor-pointer"
                  @click="toggleTestStatus"
                >
                  <span class="mr-2 h-2 w-2 rounded-full bg-slate-500" />
                  {{ t('setAsCustomer') }}
                </DropdownMenuItem>
                <DropdownMenuItem
                  v-else
                  class="cursor-pointer"
                  @click="toggleTestStatus"
                >
                  <span class="mr-2 h-2 w-2 rounded-full bg-purple-500" />
                  {{ t('setAsTestCustomer') }}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <!-- Failed Integrity Checks Banner -->
      <div
        v-if="org.has_integrity_errors && org.integrity_check_result"
        class="space-y-2"
      >
        <div
          v-for="check in (org.integrity_check_result as IntegrityCheck[]).filter((c) => !c.passed)"
          :key="check.name"
          class="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50
            dark:bg-amber-950/30"
        >
          <span
            class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-200 text-xs font-bold
              text-amber-700 dark:bg-amber-900 dark:text-amber-300"
          >
            !
          </span>
          <div class="min-w-0 flex-1">
            <div class="text-sm font-medium text-amber-800 dark:text-amber-200">
              {{ check.title || check.name.replace(/_/g, ' ') }}
            </div>
            <div
              v-if="check.action"
              class="mt-1 text-xs text-amber-700 dark:text-amber-400"
            >
              {{ check.action.replace(/<[^>]*>/g, '') }}
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <AutoSavingForm @save="handleSave">
          <div class="grid grid-cols-3 gap-8">
            <div class="col-span-2">
              <div class="mb-4 font-semibold">
                {{ t('general') }}
              </div>
              <hr class="mb-4" />
              <InputField
                v-model="org.customer_number"
                :label="t('customerNumber')"
                name="customer_number"
                :disabled="true"
              />
              <InputField
                v-model="org.name"
                :label="t('name')"
                name="name"
                :rules="yup.string().required()"
              />
              <FormGrid>
                <div class="col-span-9">
                  <InputField
                    v-model="org.street"
                    :label="t('street')"
                    name="street"
                    :rules="yup.string().required()"
                  />
                </div>
                <div class="col-span-3">
                  <InputField
                    v-model="org.street_number"
                    :label="t('house')"
                    name="street_number"
                    :rules="yup.string().max($Organisation.properties.street.maxLength).required()"
                  />
                </div>
              </FormGrid>
              <FormGrid>
                <div class="col-span-3">
                  <InputField
                    v-model="org.zip"
                    :label="t('postcode')"
                    name="zip"
                    :rules="yup.string().max($Organisation.properties.zip.maxLength).required()"
                  />
                </div>
                <div class="col-span-9">
                  <InputField
                    v-model="org.city"
                    :label="t('city')"
                    name="city"
                    :rules="yup.string().max($Organisation.properties.city.maxLength).required()"
                  />
                </div>
              </FormGrid>
              <InputField
                v-model="org.country"
                :label="t('country')"
                name="country"
                :rules="yup.string().max($Organisation.properties.country.maxLength).required()"
              />
              <InputField
                v-model="org.email"
                :label="t('email')"
                name="email"
                :rules="yup.string().email().required()"
              />
              <InputField
                v-model="org.phone"
                :label="t('phone')"
                name="phone"
                :rules="yup.string().max($Organisation.properties.phone.maxLength).required()"
              />
            </div>
            <div class="col-span-1">
              <div class="pb-3 text-base font-semibold text-gray-800 sm:text-lg">
                {{ t('connections') }}
              </div>
              <hr class="mb-4" />
              <!-- Stripe Connection Section -->
              <div class="mb-4">
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ t('stripeConnection') }}
                </label>

                <!-- Connected State: Show customer card -->
                <div
                  v-if="stripeCustomer"
                  class="rounded-lg border border-purple-200 bg-purple-50 p-3 dark:border-purple-900/50 dark:bg-purple-950/30"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2">
                        <!-- Stripe icon -->
                        <svg
                          class="h-5 w-5 shrink-0 text-purple-600"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path
                            d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"
                          />
                        </svg>
                        <span class="font-medium text-gray-900 dark:text-gray-100">
                          {{ stripeCustomer.name || t('unnamed') }}
                        </span>
                      </div>
                      <div
                        v-if="stripeCustomer.email"
                        class="mt-1 text-sm text-gray-600 dark:text-gray-400"
                      >
                        {{ stripeCustomer.email }}
                      </div>
                      <a
                        :href="stripeCustomer.stripe_url"
                        target="_blank"
                        class="mt-1 inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 hover:underline
                          dark:text-purple-400"
                        @click.stop
                      >
                        {{ t('openInStripe') }}
                        <svg
                          class="h-3 w-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    </div>
                    <div class="flex shrink-0 gap-2">
                      <button
                        type="button"
                        class="rounded-md px-2 py-1 text-xs font-medium text-purple-600 transition-colors hover:bg-purple-100
                          dark:text-purple-400 dark:hover:bg-purple-900/30"
                        @click="openStripeModal"
                      >
                        {{ t('change') }}
                      </button>
                      <button
                        type="button"
                        class="rounded-md px-2 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-100
                          dark:text-red-400 dark:hover:bg-red-900/30"
                        @click="removeStripeConnection"
                      >
                        {{ t('removeConnection') }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Not Connected State: Show connect button -->
                <div
                  v-else
                  class="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center dark:border-neutral-600
                    dark:bg-neutral-800"
                >
                  <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {{ t('stripeNotConnected') }}
                  </p>
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {{ t('stripeNotConnectedDescription') }}
                  </p>
                  <button
                    type="button"
                    class="mt-3 inline-flex items-center gap-2 rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium
                      text-white transition-colors hover:bg-purple-700 focus:outline-none focus:ring-2
                      focus:ring-purple-500 focus:ring-offset-2"
                    @click="openStripeModal"
                  >
                    <svg
                      class="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      />
                    </svg>
                    {{ t('connectStripe') }}
                  </button>
                </div>
              </div>

              <!-- HubSpot Connection Section -->
              <div class="mb-4">
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ t('hubspotConnection') }}
                </label>

                <!-- Connected State: Show company card -->
                <div
                  v-if="org.hubspot_company"
                  class="rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-900/50 dark:bg-orange-950/30"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2">
                        <!-- HubSpot icon -->
                        <svg
                          class="h-5 w-5 shrink-0 text-orange-600"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path
                            d="M17.69 14.57c-.43 0-.82.11-1.18.32l-1.95-1.47c.15-.35.24-.74.24-1.15 0-.52-.13-1.01-.36-1.44l2.01-2.01c.26.1.53.16.82.16 1.24 0 2.25-1.01 2.25-2.25S18.51 4.48 17.27 4.48 15.02 5.49 15.02 6.73c0 .29.06.57.16.82l-2.01 2.01c-.43-.23-.92-.36-1.44-.36-.78 0-1.49.27-2.05.72L6.61 7.25c.03-.12.05-.25.05-.38 0-.9-.73-1.62-1.62-1.62-.9 0-1.62.73-1.62 1.62s.73 1.62 1.62 1.62c.38 0 .72-.14 1-.36l3.06 2.67c-.23.43-.36.92-.36 1.44 0 1.66 1.27 3.02 2.89 3.17v2.45c-.65.17-1.13.76-1.13 1.46 0 .84.68 1.52 1.52 1.52s1.52-.68 1.52-1.52c0-.7-.48-1.29-1.13-1.46v-2.45c1.01-.1 1.88-.64 2.43-1.42l1.95 1.47c-.08.23-.12.47-.12.73 0 1.24 1.01 2.25 2.25 2.25s2.25-1.01 2.25-2.25-1.01-2.25-2.25-2.25z"
                          />
                        </svg>
                        <span class="font-medium text-gray-900 dark:text-gray-100">
                          {{ org.hubspot_company.name || t('unnamedCompany') }}
                        </span>
                      </div>
                      <div
                        v-if="org.hubspot_company.domain"
                        class="mt-1 text-sm text-gray-600 dark:text-gray-400"
                      >
                        {{ org.hubspot_company.domain }}
                      </div>
                      <a
                        :href="org.hubspot_company.hubspot_url"
                        target="_blank"
                        class="mt-1 inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 hover:underline
                          dark:text-orange-400"
                      >
                        <span>{{ t('openInHubSpot') }}</span>
                        <svg
                          class="h-3 w-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    </div>
                    <div class="flex shrink-0 gap-2">
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 rounded-md border border-orange-300 bg-white px-2.5 py-1.5 text-xs
                          font-medium text-orange-700 transition-colors hover:bg-orange-50 focus:outline-none focus:ring-2
                          focus:ring-orange-500 focus:ring-offset-1 dark:border-orange-700 dark:bg-orange-950
                          dark:text-orange-300 dark:hover:bg-orange-900"
                        @click="openHubspotModal"
                      >
                        {{ t('change') }}
                      </button>
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-2.5 py-1.5 text-xs
                          font-medium text-red-700 transition-colors hover:bg-red-50 focus:outline-none focus:ring-2
                          focus:ring-red-500 focus:ring-offset-1 dark:border-red-700 dark:bg-red-950 dark:text-red-300
                          dark:hover:bg-red-900"
                        @click="removeHubspotConnection"
                      >
                        {{ t('removeConnection') }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Not Connected State: Show connect button -->
                <div
                  v-else
                  class="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 text-center dark:border-neutral-600
                    dark:bg-neutral-800"
                >
                  <svg
                    class="mx-auto h-8 w-8 text-gray-400"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M17.69 14.57c-.43 0-.82.11-1.18.32l-1.95-1.47c.15-.35.24-.74.24-1.15 0-.52-.13-1.01-.36-1.44l2.01-2.01c.26.1.53.16.82.16 1.24 0 2.25-1.01 2.25-2.25S18.51 4.48 17.27 4.48 15.02 5.49 15.02 6.73c0 .29.06.57.16.82l-2.01 2.01c-.43-.23-.92-.36-1.44-.36-.78 0-1.49.27-2.05.72L6.61 7.25c.03-.12.05-.25.05-.38 0-.9-.73-1.62-1.62-1.62-.9 0-1.62.73-1.62 1.62s.73 1.62 1.62 1.62c.38 0 .72-.14 1-.36l3.06 2.67c-.23.43-.36.92-.36 1.44 0 1.66 1.27 3.02 2.89 3.17v2.45c-.65.17-1.13.76-1.13 1.46 0 .84.68 1.52 1.52 1.52s1.52-.68 1.52-1.52c0-.7-.48-1.29-1.13-1.46v-2.45c1.01-.1 1.88-.64 2.43-1.42l1.95 1.47c-.08.23-.12.47-.12.73 0 1.24 1.01 2.25 2.25 2.25s2.25-1.01 2.25-2.25-1.01-2.25-2.25-2.25z"
                    />
                  </svg>
                  <p class="mt-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                    {{ t('hubspotNotConnected') }}
                  </p>
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    {{ t('hubspotNotConnectedDescription') }}
                  </p>
                  <button
                    type="button"
                    class="mt-3 inline-flex items-center gap-2 rounded-md bg-orange-600 px-3 py-2 text-sm font-medium
                      text-white transition-colors hover:bg-orange-700 focus:outline-none focus:ring-2
                      focus:ring-orange-500 focus:ring-offset-2"
                    @click="openHubspotModal"
                  >
                    <svg
                      class="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    {{ t('connectHubspot') }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Product Cards -->
          <div class="mt-8 grid grid-cols-2 gap-6">
            <!-- JUPUS Platform Card -->
            <div
              class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
            >
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {{ t('jupusPlatform') }}
                </h3>
                <div class="shrink-0">
                  <CheckboxField
                    v-model="org.enable_cases"
                    :label="t('jupusPlatformActive')"
                    name="enable_cases"
                  />
                </div>
              </div>

              <!-- Platform Subscription Selector -->
              <div class="mb-6">
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ t('platformSubscription') }}
                </label>

                <!-- Connected State: Show subscription card -->
                <div
                  v-if="org.platform_stripe_subscription"
                  class="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-900/50 dark:bg-purple-950/30"
                >
                  <!-- Header row with status, MRR, and actions -->
                  <div class="flex items-start justify-between gap-3">
                    <div class="flex items-center gap-2">
                      <span
                        class="rounded-full px-2.5 py-1 text-xs font-semibold"
                        :class="getSubscriptionStatusColor(org.platform_stripe_subscription.status)"
                      >
                        {{ org.platform_stripe_subscription.status }}
                      </span>
                      <span class="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {{ formatCurrency(org.platform_stripe_subscription.calculated_mrr) }}/mo
                      </span>
                    </div>
                    <div class="flex shrink-0 gap-2">
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 rounded-md border border-purple-300 bg-white px-2.5 py-1.5 text-xs
                          font-medium text-purple-700 transition-colors hover:bg-purple-50 focus:outline-none focus:ring-2
                          focus:ring-purple-500 focus:ring-offset-1 dark:border-purple-700 dark:bg-purple-950
                          dark:text-purple-300 dark:hover:bg-purple-900"
                        @click="openSubscriptionModal('platform')"
                      >
                        {{ t('change') }}
                      </button>
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-2.5 py-1.5 text-xs
                          font-medium text-red-700 transition-colors hover:bg-red-50 focus:outline-none focus:ring-2
                          focus:ring-red-500 focus:ring-offset-1 dark:border-red-700 dark:bg-red-950 dark:text-red-300
                          dark:hover:bg-red-900"
                        @click="removeSubscription('platform')"
                      >
                        {{ t('removeConnection') }}
                      </button>
                    </div>
                  </div>

                  <!-- Plans -->
                  <div
                    v-if="org.platform_stripe_subscription.subscribed_plans?.length"
                    class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {{ (org.platform_stripe_subscription.subscribed_plans as string[]).join(', ') }}
                  </div>

                  <!-- Cancellation warnings -->
                  <div
                    v-if="org.platform_stripe_subscription.canceled_at"
                    class="mt-2 flex items-center gap-1.5 rounded-md bg-red-100 px-2.5 py-1.5 text-xs font-medium text-red-700
                      dark:bg-red-900/30 dark:text-red-400"
                  >
                    <svg
                      class="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    {{ t('subscriptionCanceledAt') }}:
                    {{ formatDate(org.platform_stripe_subscription.canceled_at) }}
                  </div>
                  <div
                    v-else-if="org.platform_stripe_subscription.cancel_at_period_end"
                    class="mt-2 flex items-center gap-1.5 rounded-md bg-amber-100 px-2.5 py-1.5 text-xs font-medium
                      text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                  >
                    <svg
                      class="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {{ t('subscriptionCancelAtPeriodEnd') }}
                  </div>

                  <!-- Details grid -->
                  <div
                    class="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-gray-600 dark:text-gray-400"
                  >
                    <div>
                      <span class="font-medium text-gray-500 dark:text-gray-500">
                        {{ t('subscriptionStartDate') }}:
                      </span>
                      {{ formatDate(org.platform_stripe_subscription.start_date) }}
                    </div>
                    <div v-if="org.platform_stripe_subscription.current_period_end">
                      <span class="font-medium text-gray-500 dark:text-gray-500">
                        {{ t('subscriptionCurrentPeriodEnd') }}:
                      </span>
                      {{ formatDate(org.platform_stripe_subscription.current_period_end) }}
                    </div>
                    <div v-if="org.platform_stripe_subscription.trial_start">
                      <span class="font-medium text-gray-500 dark:text-gray-500">
                        {{ t('subscriptionTrialPeriod') }}:
                      </span>
                      {{ formatDate(org.platform_stripe_subscription.trial_start) }} -
                      {{ formatDate(org.platform_stripe_subscription.trial_end) }}
                    </div>
                  </div>

                  <!-- Stripe link -->
                  <a
                    :href="org.platform_stripe_subscription.stripe_url"
                    target="_blank"
                    class="mt-3 inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 hover:underline
                      dark:text-purple-400"
                  >
                    <span>{{ org.platform_stripe_subscription.stripe_id }}</span>
                    <svg
                      class="h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                </div>

                <!-- Not Connected State: Show connect button -->
                <div
                  v-else
                  class="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 text-center dark:border-neutral-600
                    dark:bg-neutral-800"
                >
                  <svg
                    class="mx-auto h-8 w-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                  <p class="mt-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                    {{ t('subscriptionNotConnected') }}
                  </p>
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    {{ t('subscriptionNotConnectedDescription') }}
                  </p>
                  <button
                    type="button"
                    class="mt-3 inline-flex items-center gap-2 rounded-md bg-purple-600 px-3 py-2 text-sm font-medium
                      text-white transition-colors hover:bg-purple-700 focus:outline-none focus:ring-2
                      focus:ring-purple-500 focus:ring-offset-2"
                    @click="openSubscriptionModal('platform')"
                  >
                    <svg
                      class="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    {{ t('connectSubscription') }}
                  </button>
                </div>
              </div>

              <!-- Seats Row -->
              <FormGrid>
                <div class="col-span-6">
                  <InputField
                    v-model="org.seats"
                    type="number"
                    :label="t('seats')"
                    name="seats"
                    :rules="yup.number().required()"
                  />
                </div>
                <div class="col-span-6">
                  <InputField
                    v-model="org.appointments_seats"
                    type="number"
                    :label="t('appointmentBookingSeats')"
                    name="appointments_seats"
                    :rules="yup.number().required()"
                  />
                </div>
              </FormGrid>

              <!-- Features Section -->
              <div
                class="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-neutral-700 dark:bg-neutral-900"
              >
                <h4 class="mb-4 font-semibold text-gray-900 dark:text-gray-100">
                  {{ t('features') }}
                </h4>
                <div class="space-y-3">
                  <CheckboxField
                    v-model="org.enable_appointments"
                    :label="t('appointments')"
                    name="enable_appointments"
                  />
                  <CheckboxField
                    v-model="org.enable_law_specific_data"
                    :label="t('documentAI')"
                    name="enable_law_specific_data"
                  />
                  <CheckboxField
                    v-model="org.ai_enabled"
                    :label="t('aiEnabled')"
                    name="ai_enabled"
                  />
                  <CheckboxField
                    v-model="org.enable_ai_workflows"
                    :label="t('aiWorkflows')"
                    name="enable_ai_workflows"
                  />
                </div>
              </div>

              <!-- Onboarding Date/Time -->
              <div class="mt-6">
                <h4 class="mb-4 font-semibold text-gray-900 dark:text-gray-100">
                  {{ t('onboardingStartDate') }}
                </h4>
                <FormGrid>
                  <div class="col-span-8">
                    <DatePickerField
                      v-model="onboardingDate"
                      :label="t('onboardingStartDate')"
                      name="onboarding_start_date"
                      @update:model-value="saveOnboardingDateTime"
                    />
                  </div>
                  <div class="col-span-4">
                    <label class="mb-2 block text-sm font-medium">{{ t('onboardingTime') }}</label>
                    <input
                      v-model="onboardingTime"
                      type="time"
                      class="block w-full cursor-text rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:z-10
                        focus:border-purple-600 focus:ring-purple-600"
                      @change="saveOnboardingDateTime(onboardingDate)"
                    />
                  </div>
                </FormGrid>
              </div>

              <!-- Email Settings -->
              <div class="mt-6">
                <h4 class="mb-4 font-semibold text-gray-900 dark:text-gray-100">
                  {{ t('inboundEmailSlug') }}
                </h4>
                <!-- Split email field with slug input, domain suffix, and copy button -->
                <div class="mb-4">
                  <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {{ t('inboundEmailSlug') }}
                  </label>
                  <div class="flex">
                    <input
                      v-model="org.inbound_email_slug"
                      type="text"
                      class="block w-full min-w-0 flex-1 rounded-l-lg border border-r-0 border-gray-300 bg-white px-3 py-2
                        text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500
                        dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-100"
                      @change="handleSave({ inbound_email_slug: org.inbound_email_slug })"
                    />
                    <span
                      class="inline-flex items-center border border-l-0 border-gray-300 bg-gray-100 px-3 text-sm text-gray-600
                        dark:border-neutral-600 dark:bg-neutral-800 dark:text-gray-400"
                    >
                      {{ inboundEmailDomain }}
                    </span>
                    <button
                      type="button"
                      class="inline-flex items-center gap-1.5 rounded-r-lg border border-l-0 border-gray-300 bg-gray-50 px-3 py-2
                        text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none
                        focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 dark:border-neutral-600 dark:bg-neutral-700
                        dark:text-gray-300 dark:hover:bg-neutral-600"
                      @click="copyEmailToClipboard"
                    >
                      <svg
                        class="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      {{ t('copyEmail') }}
                    </button>
                  </div>
                </div>
                <CheckboxField
                  v-model="org.enable_inbound_email_replies"
                  :label="t('enableInboundEmailReplies')"
                  name="enable_inbound_email_replies"
                />
              </div>

              <!-- Platform Churn Date -->
              <div
                class="mt-6 border-t border-gray-200 pt-6 dark:border-neutral-700"
                data-cy="platform-churned-at-field"
              >
                <DatePickerField
                  v-model="platformChurnedAt"
                  :label="t('platformChurnedAt')"
                  name="platform_churned_at"
                  @update:model-value="savePlatformChurnedAt"
                />
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {{ t('platformChurnedAtHint') }}
                </p>
              </div>
            </div>

            <!-- JUPUS Phone Bot Card -->
            <div
              class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
            >
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {{ t('jupusPhoneAI') }}
                </h3>
                <div class="shrink-0">
                  <CheckboxField
                    v-model="org.enable_voice"
                    :label="t('jupusPhoneAIActive')"
                    name="enable_voice"
                  />
                </div>
              </div>

              <!-- Phonebot Subscription Selector -->
              <div class="mb-6">
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ t('phonebotSubscription') }}
                </label>

                <!-- Connected State: Show subscription card -->
                <div
                  v-if="org.phonebot_stripe_subscription"
                  class="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-900/50 dark:bg-purple-950/30"
                >
                  <!-- Header row with status, MRR, and actions -->
                  <div class="flex items-start justify-between gap-3">
                    <div class="flex items-center gap-2">
                      <span
                        class="rounded-full px-2.5 py-1 text-xs font-semibold"
                        :class="getSubscriptionStatusColor(org.phonebot_stripe_subscription.status)"
                      >
                        {{ org.phonebot_stripe_subscription.status }}
                      </span>
                      <span class="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {{ formatCurrency(org.phonebot_stripe_subscription.calculated_mrr) }}/mo
                      </span>
                    </div>
                    <div class="flex shrink-0 gap-2">
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 rounded-md border border-purple-300 bg-white px-2.5 py-1.5 text-xs
                          font-medium text-purple-700 transition-colors hover:bg-purple-50 focus:outline-none focus:ring-2
                          focus:ring-purple-500 focus:ring-offset-1 dark:border-purple-700 dark:bg-purple-950
                          dark:text-purple-300 dark:hover:bg-purple-900"
                        @click="openSubscriptionModal('phonebot')"
                      >
                        {{ t('change') }}
                      </button>
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-2.5 py-1.5 text-xs
                          font-medium text-red-700 transition-colors hover:bg-red-50 focus:outline-none focus:ring-2
                          focus:ring-red-500 focus:ring-offset-1 dark:border-red-700 dark:bg-red-950 dark:text-red-300
                          dark:hover:bg-red-900"
                        @click="removeSubscription('phonebot')"
                      >
                        {{ t('removeConnection') }}
                      </button>
                    </div>
                  </div>

                  <!-- Plans -->
                  <div
                    v-if="org.phonebot_stripe_subscription.subscribed_plans?.length"
                    class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {{ (org.phonebot_stripe_subscription.subscribed_plans as string[]).join(', ') }}
                  </div>

                  <!-- Cancellation warnings -->
                  <div
                    v-if="org.phonebot_stripe_subscription.canceled_at"
                    class="mt-2 flex items-center gap-1.5 rounded-md bg-red-100 px-2.5 py-1.5 text-xs font-medium text-red-700
                      dark:bg-red-900/30 dark:text-red-400"
                  >
                    <svg
                      class="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    {{ t('subscriptionCanceledAt') }}:
                    {{ formatDate(org.phonebot_stripe_subscription.canceled_at) }}
                  </div>
                  <div
                    v-else-if="org.phonebot_stripe_subscription.cancel_at_period_end"
                    class="mt-2 flex items-center gap-1.5 rounded-md bg-amber-100 px-2.5 py-1.5 text-xs font-medium
                      text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                  >
                    <svg
                      class="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {{ t('subscriptionCancelAtPeriodEnd') }}
                  </div>

                  <!-- Details grid -->
                  <div
                    class="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-gray-600 dark:text-gray-400"
                  >
                    <div>
                      <span class="font-medium text-gray-500 dark:text-gray-500">
                        {{ t('subscriptionStartDate') }}:
                      </span>
                      {{ formatDate(org.phonebot_stripe_subscription.start_date) }}
                    </div>
                    <div v-if="org.phonebot_stripe_subscription.current_period_end">
                      <span class="font-medium text-gray-500 dark:text-gray-500">
                        {{ t('subscriptionCurrentPeriodEnd') }}:
                      </span>
                      {{ formatDate(org.phonebot_stripe_subscription.current_period_end) }}
                    </div>
                    <div v-if="org.phonebot_stripe_subscription.trial_start">
                      <span class="font-medium text-gray-500 dark:text-gray-500">
                        {{ t('subscriptionTrialPeriod') }}:
                      </span>
                      {{ formatDate(org.phonebot_stripe_subscription.trial_start) }} -
                      {{ formatDate(org.phonebot_stripe_subscription.trial_end) }}
                    </div>
                  </div>

                  <!-- Stripe link -->
                  <a
                    :href="org.phonebot_stripe_subscription.stripe_url"
                    target="_blank"
                    class="mt-3 inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 hover:underline
                      dark:text-purple-400"
                  >
                    <span>{{ org.phonebot_stripe_subscription.stripe_id }}</span>
                    <svg
                      class="h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                </div>

                <!-- Not Connected State: Show connect button -->
                <div
                  v-else
                  class="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 text-center dark:border-neutral-600
                    dark:bg-neutral-800"
                >
                  <svg
                    class="mx-auto h-8 w-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                  <p class="mt-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                    {{ t('subscriptionNotConnected') }}
                  </p>
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    {{ t('subscriptionNotConnectedDescription') }}
                  </p>
                  <button
                    type="button"
                    class="mt-3 inline-flex items-center gap-2 rounded-md bg-purple-600 px-3 py-2 text-sm font-medium
                      text-white transition-colors hover:bg-purple-700 focus:outline-none focus:ring-2
                      focus:ring-purple-500 focus:ring-offset-2"
                    @click="openSubscriptionModal('phonebot')"
                  >
                    <svg
                      class="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    {{ t('connectSubscription') }}
                  </button>
                </div>
              </div>

              <!-- Phonebot Settings -->
              <CheckboxField
                v-model="org.is_phonebot_self_service_customer"
                :label="t('isPhonebotSelfServiceCustomer')"
                name="is_phonebot_self_service_customer"
              />
              <SelectField
                v-model="org.phonebot_self_service_plan"
                name="phonebot_self_service_plan"
                :label="t('phonebotSelfServicePlan')"
                :options="[
                  { value: '', title: '-' },
                  { value: 'phone_beginner_plan', title: 'Phone Beginner' },
                  { value: 'phone_professional_plan', title: 'Phone Professional' },
                  { value: 'phone_professional_plus_plan', title: 'Phone Professional Plus' },
                  { value: 'phone_enterprise_plan', title: 'Phone Enterprise' }
                ]"
              />
              <InputField
                v-model="org.phonebot_self_service_plan_enterprise_included_minutes"
                type="number"
                :label="t('phonebotEnterpriseIncludedMinutes')"
                name="phonebot_self_service_plan_enterprise_included_minutes"
                :rules="yup.number().min(0)"
              />
              <SelectField
                v-model="org.default_language"
                name="default_language"
                :label="t('phonebotLanguage')"
                :options="[
                  { value: 'de', title: 'Deutsch' },
                  { value: 'fr', title: 'Französisch' },
                  { value: 'en', title: 'Englisch' }
                ]"
              />

              <!-- Phonebot Churn Date -->
              <div class="mt-6 border-t border-gray-200 pt-6 dark:border-neutral-700">
                <DatePickerField
                  v-model="phonebotChurnedAt"
                  :label="t('phonebotChurnedAt')"
                  name="phonebot_churned_at"
                  @update:model-value="savePhonebotChurnedAt"
                />
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {{ t('phonebotChurnedAtHint') }}
                </p>
              </div>
            </div>
          </div>

          <!-- Law Fields Widget -->
          <div
            class="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
              dark:bg-neutral-800"
          >
            <h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {{ t('lawFields') }}
            </h3>
            <MultiSelectField
              v-model="org.law_fields"
              :options="lawFieldOptions"
              name="law_fields"
            />
          </div>
        </AutoSavingForm>
      </div>
    </template>

    <!-- Integrity Check Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showIntegrityModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          @click.self="closeIntegrityModal"
        >
          <div
            class="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-2xl
              dark:bg-neutral-800"
          >
            <!-- Modal Header -->
            <div
              class="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-neutral-700"
            >
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {{ t('integrityCheck') }}
              </h3>
              <button
                type="button"
                class="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600
                  dark:hover:bg-neutral-700 dark:hover:text-gray-300"
                @click="closeIntegrityModal"
              >
                <svg
                  class="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <!-- Modal Content -->
            <div class="flex-1 overflow-y-auto px-6 py-4">
              <!-- Loading state -->
              <div
                v-if="isRunningIntegrityCheck"
                class="flex flex-col items-center justify-center py-12"
              >
                <svg
                  class="h-8 w-8 animate-spin text-purple-600"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                <p class="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  {{ t('integrityCheckRunning') }}
                </p>
              </div>

              <!-- Results -->
              <div
                v-else
                class="space-y-2"
              >
                <div
                  v-if="integrityChecks.length === 0"
                  class="flex flex-col items-center justify-center py-12 text-center"
                >
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ t('noIntegrityChecks') }}
                  </p>
                </div>
                <div
                  v-for="check in integrityChecks"
                  :key="check.name"
                  class="flex items-start gap-3 rounded-lg border p-3"
                  :class="
                    check.passed
                      ? 'border-green-200 bg-green-50 dark:border-green-900/50 dark:bg-green-950/30'
                      : 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30'
                  "
                >
                  <span
                    class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                    :class="
                      check.passed
                        ? 'bg-green-200 text-green-700 dark:bg-green-900 dark:text-green-300'
                        : 'bg-amber-200 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                    "
                  >
                    {{ check.passed ? '✓' : '!' }}
                  </span>
                  <div class="min-w-0 flex-1">
                    <div
                      class="text-sm font-medium"
                      :class="
                        check.passed
                          ? 'text-green-800 dark:text-green-200'
                          : 'text-amber-800 dark:text-amber-200'
                      "
                    >
                      {{ check.name.replace(/_/g, ' ') }}
                    </div>
                    <div
                      v-if="!check.passed && check.title"
                      class="mt-0.5 text-sm"
                      :class="'text-amber-700 dark:text-amber-300'"
                    >
                      {{ check.title }}
                    </div>
                    <div
                      v-if="!check.passed && check.action"
                      class="mt-1 text-xs text-gray-600 dark:text-gray-400"
                    >
                      {{ check.action.replace(/<[^>]*>/g, '') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Modal Footer -->
            <div
              class="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-neutral-700"
            >
              <button
                type="button"
                class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700
                  transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500
                  focus:ring-offset-2 dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-300
                  dark:hover:bg-neutral-600"
                @click="closeIntegrityModal"
              >
                {{ t('close') }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- HubSpot Company Selection Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showHubspotModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          @click.self="closeHubspotModal"
        >
          <Transition
            enter-active-class="duration-200 ease-out"
            enter-from-class="scale-95 opacity-0"
            enter-to-class="scale-100 opacity-100"
            leave-active-class="duration-150 ease-in"
            leave-from-class="scale-100 opacity-100"
            leave-to-class="scale-95 opacity-0"
          >
            <div
              v-if="showHubspotModal"
              data-cy="hubspot-modal"
              class="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl
                dark:bg-neutral-800"
            >
              <!-- Modal Header -->
              <div
                class="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-neutral-700"
              >
                <div class="flex items-center gap-3">
                  <div class="rounded-lg bg-orange-100 p-2 dark:bg-orange-900/30">
                    <svg
                      class="h-5 w-5 text-orange-600"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M17.69 14.57c-.43 0-.82.11-1.18.32l-1.95-1.47c.15-.35.24-.74.24-1.15 0-.52-.13-1.01-.36-1.44l2.01-2.01c.26.1.53.16.82.16 1.24 0 2.25-1.01 2.25-2.25S18.51 4.48 17.27 4.48 15.02 5.49 15.02 6.73c0 .29.06.57.16.82l-2.01 2.01c-.43-.23-.92-.36-1.44-.36-.78 0-1.49.27-2.05.72L6.61 7.25c.03-.12.05-.25.05-.38 0-.9-.73-1.62-1.62-1.62-.9 0-1.62.73-1.62 1.62s.73 1.62 1.62 1.62c.38 0 .72-.14 1-.36l3.06 2.67c-.23.43-.36.92-.36 1.44 0 1.66 1.27 3.02 2.89 3.17v2.45c-.65.17-1.13.76-1.13 1.46 0 .84.68 1.52 1.52 1.52s1.52-.68 1.52-1.52c0-.7-.48-1.29-1.13-1.46v-2.45c1.01-.1 1.88-.64 2.43-1.42l1.95 1.47c-.08.23-.12.47-.12.73 0 1.24 1.01 2.25 2.25 2.25s2.25-1.01 2.25-2.25-1.01-2.25-2.25-2.25z"
                      />
                    </svg>
                  </div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {{ t('hubspotModalTitle') }}
                  </h3>
                </div>
                <button
                  type="button"
                  class="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600
                    dark:hover:bg-neutral-700 dark:hover:text-gray-300"
                  @click="closeHubspotModal"
                >
                  <svg
                    class="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <!-- Modal Content -->
              <div class="flex-1 overflow-y-auto px-6 py-4">
                <!-- Suggested Matches Section -->
                <div class="mb-6">
                  <div class="mb-2 flex items-center gap-2">
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {{ t('suggestedMatches') }}
                    </h4>
                    <span
                      v-if="hubspotCandidates.length > 0"
                      class="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30
                        dark:text-orange-300"
                    >
                      {{ hubspotCandidates.length }}
                    </span>
                  </div>
                  <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
                    {{ t('suggestedMatchesDescription') }}
                  </p>

                  <div
                    v-if="isSearchingCandidates"
                    class="flex items-center justify-center py-6"
                  >
                    <svg
                      class="h-6 w-6 animate-spin text-orange-600"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                  </div>

                  <div
                    v-else-if="hubspotCandidates.length === 0"
                    class="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-4 text-center dark:border-neutral-600
                      dark:bg-neutral-800"
                  >
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      {{ t('noSuggestionsFound') }}
                    </p>
                  </div>

                  <div
                    v-else
                    class="space-y-2"
                  >
                    <div
                      v-for="candidate in hubspotCandidates"
                      :key="candidate.uuid"
                      class="group flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50 p-3
                        transition-colors hover:border-orange-300 hover:bg-orange-100 dark:border-orange-900/50
                        dark:bg-orange-950/30 dark:hover:border-orange-800 dark:hover:bg-orange-950/50"
                    >
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          <span class="font-medium text-gray-900 dark:text-gray-100">
                            {{ candidate.name || t('unnamed') }}
                          </span>
                          <span
                            v-if="candidate.domain"
                            class="rounded bg-orange-200 px-1.5 py-0.5 text-xs text-orange-800 dark:bg-orange-900/50
                              dark:text-orange-200"
                          >
                            {{ candidate.domain }}
                          </span>
                        </div>
                        <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {{ t('matched') }}: {{ candidate.matched_domains.join(', ') }}
                        </div>
                      </div>
                      <button
                        type="button"
                        class="shrink-0 rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white transition-colors
                          hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2
                          disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="isSelectingHubspotCompany !== null"
                        @click="selectHubspotCompany(candidate)"
                      >
                        <span
                          v-if="isSelectingHubspotCompany === candidate.uuid"
                          class="flex items-center gap-1.5"
                        >
                          <svg
                            class="h-3.5 w-3.5 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            />
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                        </span>
                        <span v-else>{{ t('selectCompany') }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Divider -->
                <div class="relative mb-6">
                  <div class="absolute inset-0 flex items-center">
                    <div class="w-full border-t border-gray-200 dark:border-neutral-700" />
                  </div>
                  <div class="relative flex justify-center">
                    <span
                      class="bg-white px-3 text-xs text-gray-500 dark:bg-neutral-800 dark:text-gray-400"
                    >
                      {{ t('orSearchAllCompanies') }}
                    </span>
                  </div>
                </div>

                <!-- Search All Companies Section -->
                <div>
                  <h4 class="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {{ t('allCompanies') }}
                  </h4>

                  <!-- Search Input -->
                  <div class="relative mb-3">
                    <div
                      class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                    >
                      <svg
                        class="h-4 w-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <input
                      v-model="hubspotSearchQuery"
                      type="text"
                      :placeholder="t('searchPlaceholder')"
                      class="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900
                        placeholder-gray-500 transition-colors focus:border-orange-500 focus:outline-none focus:ring-1
                        focus:ring-orange-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-100
                        dark:placeholder-gray-400"
                    />
                    <div
                      v-if="isSearchingAll"
                      class="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      <svg
                        class="h-4 w-4 animate-spin text-orange-600"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="4"
                        />
                        <path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                    </div>
                  </div>

                  <!-- Search Results -->
                  <div
                    v-if="
                      hubspotSearchQuery && !isSearchingAll && hubspotSearchResults.length === 0
                    "
                    class="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-4 text-center dark:border-neutral-600
                      dark:bg-neutral-800"
                  >
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      {{ t('noResultsFound') }}
                    </p>
                  </div>

                  <div
                    v-else-if="hubspotSearchResults.length > 0"
                    class="max-h-60 space-y-2 overflow-y-auto"
                  >
                    <div
                      v-for="company in hubspotSearchResults"
                      :key="company.uuid"
                      class="dark:hover:bg-neutral-750 group flex items-center justify-between rounded-lg border border-gray-200
                        bg-white p-3 transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-neutral-700
                        dark:bg-neutral-800 dark:hover:border-neutral-600"
                    >
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          <span class="font-medium text-gray-900 dark:text-gray-100">
                            {{ company.name || t('unnamed') }}
                          </span>
                          <span
                            v-if="company.domain"
                            class="rounded bg-gray-200 px-1.5 py-0.5 text-xs text-gray-700 dark:bg-neutral-700 dark:text-gray-300"
                          >
                            {{ company.domain }}
                          </span>
                        </div>
                        <a
                          :href="company.hubspot_url"
                          target="_blank"
                          class="mt-1 inline-block text-xs text-gray-500 hover:text-orange-600 hover:underline dark:text-gray-400"
                          @click.stop
                        >
                          {{ company.hubspot_url }}
                        </a>
                      </div>
                      <button
                        type="button"
                        class="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700
                          transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500
                          focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-600
                          dark:bg-neutral-700 dark:text-gray-300 dark:hover:bg-neutral-600"
                        :disabled="isSelectingHubspotCompany !== null"
                        @click="selectHubspotCompany(company)"
                      >
                        <span
                          v-if="isSelectingHubspotCompany === company.uuid"
                          class="flex items-center gap-1.5"
                        >
                          <svg
                            class="h-3.5 w-3.5 animate-spin text-orange-600"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            />
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                        </span>
                        <span v-else>{{ t('selectCompany') }}</span>
                      </button>
                    </div>
                  </div>

                  <div
                    v-else-if="!hubspotSearchQuery"
                    class="py-4 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    {{ t('typeToSearch') }}
                  </div>
                </div>
              </div>

              <!-- Modal Footer -->
              <div
                class="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-neutral-700"
              >
                <button
                  type="button"
                  class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700
                    transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500
                    focus:ring-offset-2 dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-300
                    dark:hover:bg-neutral-600"
                  @click="closeHubspotModal"
                >
                  {{ t('close') }}
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>

    <!-- Stripe Customer Selection Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showStripeModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          @click.self="closeStripeModal"
        >
          <Transition
            enter-active-class="duration-200 ease-out"
            enter-from-class="scale-95 opacity-0"
            enter-to-class="scale-100 opacity-100"
            leave-active-class="duration-150 ease-in"
            leave-from-class="scale-100 opacity-100"
            leave-to-class="scale-95 opacity-0"
          >
            <div
              v-if="showStripeModal"
              data-cy="stripe-modal"
              class="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl
                dark:bg-neutral-800"
            >
              <!-- Modal Header -->
              <div
                class="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-neutral-700"
              >
                <div class="flex items-center gap-3">
                  <div class="rounded-lg bg-purple-100 p-2 dark:bg-purple-900/30">
                    <svg
                      class="h-5 w-5 text-purple-600"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"
                      />
                    </svg>
                  </div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {{ t('stripeModalTitle') }}
                  </h3>
                </div>
                <button
                  type="button"
                  class="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600
                    dark:hover:bg-neutral-700 dark:hover:text-gray-300"
                  @click="closeStripeModal"
                >
                  <svg
                    class="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <!-- Modal Content -->
              <div class="flex-1 overflow-y-auto px-6 py-4">
                <!-- Suggested Matches Section -->
                <div class="mb-6">
                  <div class="mb-2 flex items-center gap-2">
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {{ t('stripeSuggestedMatches') }}
                    </h4>
                    <span
                      v-if="stripeCandidates.length > 0"
                      class="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30
                        dark:text-purple-300"
                    >
                      {{ stripeCandidates.length }}
                    </span>
                  </div>
                  <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
                    {{ t('stripeSuggestedMatchesDescription') }}
                  </p>

                  <div
                    v-if="isSearchingStripeCandidates"
                    class="flex items-center justify-center py-6"
                  >
                    <svg
                      class="h-6 w-6 animate-spin text-purple-600"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                  </div>

                  <div
                    v-else-if="stripeCandidates.length === 0"
                    class="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-4 text-center dark:border-neutral-600
                      dark:bg-neutral-800"
                  >
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      {{ t('noStripeSuggestionsFound') }}
                    </p>
                  </div>

                  <div
                    v-else
                    class="space-y-2"
                  >
                    <div
                      v-for="candidate in stripeCandidates"
                      :key="candidate.uuid"
                      class="group flex items-center justify-between rounded-lg border border-purple-200 bg-purple-50 p-3
                        transition-colors hover:border-purple-300 hover:bg-purple-100 dark:border-purple-900/50
                        dark:bg-purple-950/30 dark:hover:border-purple-800 dark:hover:bg-purple-950/50"
                    >
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          <span class="font-medium text-gray-900 dark:text-gray-100">
                            {{ candidate.name || t('unnamed') }}
                          </span>
                          <span
                            v-if="candidate.email"
                            class="rounded bg-purple-200 px-1.5 py-0.5 text-xs text-purple-800 dark:bg-purple-900/50
                              dark:text-purple-200"
                          >
                            {{ candidate.email }}
                          </span>
                        </div>
                        <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {{ t('matched') }}: {{ candidate.matched_domains.join(', ') }}
                        </div>
                      </div>
                      <button
                        type="button"
                        class="shrink-0 rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium text-white transition-colors
                          hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                          disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="isSelectingStripeCustomer !== null"
                        @click="selectStripeCustomer(candidate)"
                      >
                        <span
                          v-if="isSelectingStripeCustomer === candidate.uuid"
                          class="flex items-center gap-1.5"
                        >
                          <svg
                            class="h-3.5 w-3.5 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            />
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                        </span>
                        <span v-else>{{ t('selectCustomer') }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Divider -->
                <div class="relative mb-6">
                  <div class="absolute inset-0 flex items-center">
                    <div class="w-full border-t border-gray-200 dark:border-neutral-700" />
                  </div>
                  <div class="relative flex justify-center">
                    <span
                      class="bg-white px-3 text-xs text-gray-500 dark:bg-neutral-800 dark:text-gray-400"
                    >
                      {{ t('orSearchAllStripeCustomers') }}
                    </span>
                  </div>
                </div>

                <!-- Search All Customers Section -->
                <div>
                  <h4 class="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {{ t('allStripeCustomers') }}
                  </h4>

                  <!-- Search Input -->
                  <div class="relative mb-3">
                    <div
                      class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                    >
                      <svg
                        class="h-4 w-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <input
                      v-model="stripeSearchQuery"
                      type="text"
                      :placeholder="t('stripeSearchPlaceholder')"
                      class="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900
                        placeholder-gray-500 transition-colors focus:border-purple-500 focus:outline-none focus:ring-1
                        focus:ring-purple-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-100
                        dark:placeholder-gray-400"
                    />
                    <div
                      v-if="isSearchingStripeAll"
                      class="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      <svg
                        class="h-4 w-4 animate-spin text-purple-600"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="4"
                        />
                        <path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                    </div>
                  </div>

                  <!-- Search Results -->
                  <div
                    v-if="
                      stripeSearchQuery && !isSearchingStripeAll && stripeSearchResults.length === 0
                    "
                    class="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-4 text-center dark:border-neutral-600
                      dark:bg-neutral-800"
                  >
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      {{ t('noStripeResultsFound') }}
                    </p>
                  </div>

                  <div
                    v-else-if="stripeSearchResults.length > 0"
                    class="max-h-60 space-y-2 overflow-y-auto"
                  >
                    <div
                      v-for="customer in stripeSearchResults"
                      :key="customer.uuid"
                      class="dark:hover:bg-neutral-750 group flex items-center justify-between rounded-lg border border-gray-200
                        bg-white p-3 transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-neutral-700
                        dark:bg-neutral-800 dark:hover:border-neutral-600"
                    >
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          <span class="font-medium text-gray-900 dark:text-gray-100">
                            {{ customer.name || t('unnamed') }}
                          </span>
                          <span
                            v-if="customer.email"
                            class="rounded bg-gray-200 px-1.5 py-0.5 text-xs text-gray-700 dark:bg-neutral-700 dark:text-gray-300"
                          >
                            {{ customer.email }}
                          </span>
                        </div>
                        <a
                          :href="customer.stripe_url"
                          target="_blank"
                          class="mt-1 inline-block text-xs text-gray-500 hover:text-purple-600 hover:underline dark:text-gray-400"
                          @click.stop
                        >
                          {{ customer.stripe_url }}
                        </a>
                      </div>
                      <button
                        type="button"
                        class="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700
                          transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500
                          focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-600
                          dark:bg-neutral-700 dark:text-gray-300 dark:hover:bg-neutral-600"
                        :disabled="isSelectingStripeCustomer !== null"
                        @click="selectStripeCustomer(customer)"
                      >
                        <span
                          v-if="isSelectingStripeCustomer === customer.uuid"
                          class="flex items-center gap-1.5"
                        >
                          <svg
                            class="h-3.5 w-3.5 animate-spin text-purple-600"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            />
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                        </span>
                        <span v-else>{{ t('selectCustomer') }}</span>
                      </button>
                    </div>
                  </div>

                  <p
                    v-else-if="!stripeSearchQuery"
                    class="text-center text-xs text-gray-400 dark:text-gray-500"
                  >
                    {{ t('stripeTypeToSearch') }}
                  </p>
                </div>
              </div>

              <!-- Modal Footer -->
              <div
                class="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-neutral-700"
              >
                <button
                  type="button"
                  class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700
                    transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500
                    focus:ring-offset-2 dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-300
                    dark:hover:bg-neutral-600"
                  @click="closeStripeModal"
                >
                  {{ t('close') }}
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>

    <!-- Subscription Selection Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showSubscriptionModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          @click.self="closeSubscriptionModal"
        >
          <Transition
            enter-active-class="duration-200 ease-out"
            enter-from-class="scale-95 opacity-0"
            enter-to-class="scale-100 opacity-100"
            leave-active-class="duration-150 ease-in"
            leave-from-class="scale-100 opacity-100"
            leave-to-class="scale-95 opacity-0"
          >
            <div
              v-if="showSubscriptionModal"
              data-cy="subscription-modal"
              class="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl
                dark:bg-neutral-800"
            >
              <!-- Modal Header -->
              <div
                class="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-neutral-700"
              >
                <div class="flex items-center gap-3">
                  <div class="rounded-lg bg-purple-100 p-2 dark:bg-purple-900/30">
                    <svg
                      class="h-5 w-5 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                  </div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {{ t('subscriptionModalTitle') }} -
                    {{
                      subscriptionModalType === 'platform'
                        ? t('platformSubscription')
                        : t('phonebotSubscription')
                    }}
                  </h3>
                </div>
                <button
                  type="button"
                  class="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600
                    dark:hover:bg-neutral-700 dark:hover:text-gray-300"
                  @click="closeSubscriptionModal"
                >
                  <svg
                    class="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <!-- Modal Content -->
              <div class="flex-1 overflow-y-auto px-6 py-4">
                <h4 class="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {{ t('availableSubscriptions') }}
                </h4>

                <!-- Loading state -->
                <div
                  v-if="isLoadingSubscriptions"
                  class="flex flex-col items-center justify-center py-12"
                >
                  <svg
                    class="h-8 w-8 animate-spin text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  <p class="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    {{ t('loadingSubscriptions') }}
                  </p>
                </div>

                <!-- No subscriptions found -->
                <div
                  v-else-if="availableSubscriptions.length === 0"
                  class="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-8 text-center dark:border-neutral-600
                    dark:bg-neutral-800"
                >
                  <svg
                    class="mx-auto h-10 w-10 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {{ t('noSubscriptionsFound') }}
                  </p>
                </div>

                <!-- Subscription list -->
                <div
                  v-else
                  class="space-y-3"
                >
                  <div
                    v-for="subscription in availableSubscriptions"
                    :key="subscription.uuid"
                    class="group rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-purple-300
                      hover:bg-purple-50 dark:border-neutral-700 dark:bg-neutral-800 dark:hover:border-purple-700
                      dark:hover:bg-purple-950/30"
                  >
                    <div class="flex items-start justify-between gap-4">
                      <div class="min-w-0 flex-1">
                        <!-- Status and MRR -->
                        <div class="flex flex-wrap items-center gap-2">
                          <span
                            class="rounded-full px-2 py-0.5 text-xs font-medium"
                            :class="getSubscriptionStatusColor(subscription.status)"
                          >
                            {{ subscription.status }}
                          </span>
                          <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {{ formatCurrency(subscription.calculated_mrr) }}/mo
                          </span>
                          <span class="text-xs text-gray-500 dark:text-gray-400">
                            ({{ formatCurrency(subscription.calculated_arr) }}/yr)
                          </span>
                        </div>

                        <!-- Plans -->
                        <div
                          v-if="subscription.subscribed_plans?.length"
                          class="mt-2"
                        >
                          <div class="flex flex-wrap gap-1">
                            <span
                              v-for="(plan, index) in subscription.subscribed_plans as string[]"
                              :key="index"
                              class="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-700 dark:bg-neutral-700 dark:text-gray-300"
                            >
                              {{ plan }}
                            </span>
                          </div>
                        </div>

                        <!-- Dates -->
                        <div
                          class="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400"
                        >
                          <div>
                            <span class="font-medium">{{ t('subscriptionStartDate') }}:</span>
                            {{ formatDate(subscription.start_date) }}
                          </div>
                          <div v-if="subscription.trial_start">
                            <span class="font-medium">Trial:</span>
                            {{ formatDate(subscription.trial_start) }} -
                            {{ formatDate(subscription.trial_end) }}
                          </div>
                          <div v-if="subscription.canceled_at">
                            <span class="font-medium text-red-600 dark:text-red-400">
                              {{ t('subscriptionCanceledAt') }}:
                            </span>
                            {{ formatDate(subscription.canceled_at) }}
                          </div>
                          <div v-if="subscription.cancel_at_period_end">
                            <span class="font-medium text-amber-600 dark:text-amber-400">
                              Cancels at period end
                            </span>
                          </div>
                        </div>

                        <!-- Stripe link -->
                        <a
                          :href="subscription.stripe_url"
                          target="_blank"
                          class="mt-2 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 hover:underline
                            dark:text-gray-400 dark:hover:text-gray-300"
                          @click.stop
                        >
                          {{ subscription.stripe_id }}
                          <svg
                            class="h-3 w-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                        </a>
                      </div>

                      <!-- Select button -->
                      <button
                        type="button"
                        class="shrink-0 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors
                          hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                          disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="isSelectingSubscription !== null"
                        @click="selectSubscription(subscription)"
                      >
                        <span
                          v-if="isSelectingSubscription === subscription.uuid"
                          class="flex items-center gap-1.5"
                        >
                          <svg
                            class="h-4 w-4 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            />
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                        </span>
                        <span v-else>{{ t('selectCompany') }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Modal Footer -->
              <div
                class="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-neutral-700"
              >
                <button
                  type="button"
                  class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700
                    transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500
                    focus:ring-offset-2 dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-300
                    dark:hover:bg-neutral-600"
                  @click="closeSubscriptionModal"
                >
                  {{ t('close') }}
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped></style>
